import { defineRoute } from './types';
import { selectUser } from 'slices/userSlice';
import {
  selectBestPlanOption,
  selectCanSellMedSuppInState,
  selectGuaranteedEnrollment,
  selectHasMapdAndMedSuppPlans,
  selectIsKnownNotMarried,
  selectIsMarried,
  selectIsMedSuppComplicated,
  selectIsUserLoggedIn,
  selectLastButtonClicked,
  selectEligibleCoverageTypes,
  selectMedSuppEligibility,
  selectMedSuppEligibilityRouter,
  selectPathfinderThirdMilestone,
  selectPreferredShoppingYear,
  plansToCartForType,
  selectRecommendationPlansByYear,
  selectSixMonthWindowCheck,
} from 'slices/selectors';
import { medSuppSlug } from './medSuppSlug';
import {
  selectEligibilityCase,
  setEligibilityCase,
} from 'slices/eligibilitySlice';
import { setDynamicValues } from 'slices/dynamicSlice';
import { setSelectedPlanYear } from 'slices/availabilitySlice';
import { planYearForShoppingPreference } from 'lib/planYears';
import { addToCart } from 'slices/cartSlice';
import { setRecommendedType } from 'slices/recommendationSlice';
import { saveRecommendationType } from 'actions/Database';
import { parseYesNoValue } from 'utils/yesNo';
import { captureRecommendationReached } from 'lib/analytics';

/* Helper Functions, probably should be somewhere else eventually */
const directLink = (slug: string) => defineRoute({ resolve: () => slug });

const firstMilestone = defineRoute({
  selector: (state) =>
    selectUser(state).medicare_education_completion_count ?? 0,
  resolve: (count) => (count >= 1 ? 'fm' : 'ec'),
});

const sendOutCheck = defineRoute({
  selector: (state) => selectIsUserLoggedIn(state),
  resolve: (data) => {
    if (data === true) {
      return 'pi';
    }

    return 'caq';
  },
});

const deadEnd = defineRoute({
  resolve: () => null,
});

function shoppingYearChoice(lastButtonClicked: string | undefined): {
  slug: string;
  preferredShoppingYear: 'this' | 'next' | 'both';
} {
  if (lastButtonClicked === 'coverage_for_the_rest_of_this_year') {
    return { slug: 'fcty', preferredShoppingYear: 'this' };
  }
  if (lastButtonClicked === 'coverage_for_upcoming_year') {
    return { slug: 'fcny', preferredShoppingYear: 'next' };
  }
  return { slug: 'fcby', preferredShoppingYear: 'both' };
}

function coverageYearSlug(
  preferredShoppingYear: ReturnType<typeof selectPreferredShoppingYear>
): string {
  if (preferredShoppingYear === 'this') {
    return 'cty';
  }

  return 'cny';
}

const medSuppEntry = defineRoute({
  selector: selectMedSuppEligibilityRouter,
  resolve: ({ eligibilityCase }) => medSuppSlug(eligibilityCase),
  effect: ({ eligibilityCase }, dispatch) =>
    dispatch(setEligibilityCase(eligibilityCase)),
});

const checkMedSuppAvailability = defineRoute({
  selector: (state) => ({
    status: selectMedSuppEligibility(state).status,
    isComplicatedState: selectIsMedSuppComplicated(state),
    canSellMedSupp: selectCanSellMedSuppInState(state),
  }),
  resolve: ({ status, isComplicatedState, canSellMedSupp }) => {
    if (isComplicatedState || !canSellMedSupp) {
      return 'dsms';
    }

    return status === 'past' ? 'pew' : 'pef';
  },
});

function sixMonthWindowSlug({
  inWithinSixMonths,
  inState,
  isStateYearRound,
}: ReturnType<typeof selectSixMonthWindowCheck>): string {
  if (isStateYearRound) {
    return 'sbwyre';
  }
  if (inState) {
    return 'sbwnyre';
  }
  if (inWithinSixMonths) {
    return 'aismw';
  }
  return 'sm';
}

const routes = {
  // Start of Conversation
  '/check-audio': directLink('i10'),
  '/i10': directLink('i20'),
  '/i20': directLink('i30'),
  '/i30': directLink('zcq'),
  // The nma routing is handled in LocationForm
  '/zcq': directLink('oex'),
  '/oex': directLink('oe'),
  '/oe': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      // These are mis-codenamed in Kentio,
      // but as of 5/7/26 this are the correct destinations
      if (data === 'no') {
        return 'mabe';
      }
      if (data === 'yes__a_quick_one_') {
        return 'lve';
      }
      if (data === 'can_i_see_an_in_depth_version_') {
        return 'fm';
      }

      return null;
    },
  }),
  '/mabe': directLink('cbn'),
  '/cbn': directLink('mabem'),
  '/mabem': directLink('pq'),
  '/pq': directLink('msma1'),
  '/msma1': directLink('msma2'),
  '/msma2': firstMilestone,
  '/lve': firstMilestone,
  '/ec': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'education_check___yes') {
        return 'fm';
      }
      return 're';
    },
  }),
  '/re': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'yes__the_short_version___repeat_education') {
        return 'mabe';
      }
      if (data === 'yes__the_long_version___repeat_education') {
        return 'lve';
      }
      if (data === 'no__let_s_move_on___repeat_education') {
        return 'fm';
      }

      return null;
    },
  }),
  '/fm': defineRoute({
    // Marital status is only NOT relevant to mention (ccq) when we know
    // for sure the user isn't married. If they're married, or we simply
    // don't know yet (e.g. a fresh account), spouse coverage may still be
    // relevant, so we mention it (ccqunm).
    selector: (state) => selectIsKnownNotMarried(state),
    resolve: (isKnownNotMarried) => (isKnownNotMarried ? 'ccqunm' : 'ccq'),
  }),
  '/ccq': directLink('aym'),
  '/ccqunm': directLink('aym'),
  '/aym': directLink('in50'),
  '/in50': defineRoute({
    // aym (Married?) just set lastButtonClicked. Prefer that over a stale
    // currently_married profile value so "No" never routes to the spouse VO.
    selector: (state) => ({
      lastClicked: selectLastButtonClicked(state),
      isMarried: selectIsMarried(state),
    }),
    resolve: ({ lastClicked, isMarried }) => {
      const fromAym = parseYesNoValue(lastClicked);
      const married = fromAym ?? isMarried;
      return married ? 'uas' : 'i60';
    },
  }),
  '/uas': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'yes___check_uas_for_creditable_coverage') {
        return 'ccexp';
      }

      return 'nccexp';
    },
  }),
  '/i60': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'yes___check_uas_for_creditable_coverage') {
        return 'ccexp';
      }

      return 'nccexp';
    },
  }),
  '/ccexp': directLink('sfr'),
  '/sfr': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'still_wants_to_look_for_plans__yes') {
        return 'ceab';
      }

      return 'oth';
    },
  }),
  '/oth': directLink('index'),
  '/nccexp': directLink('i70'),
  '/i70': directLink('ceab'),
  '/ceab': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'check_if_enrolled_in_a_and_b___yes') {
        return 'humn';
      }

      return 'humn2';
    },
  }),
  '/humn': directLink('ccosu'),
  '/humn2': directLink('ccosu2'),
  '/ccosu': directLink('frf10'),
  '/ccosu2': directLink('frf10'),
  // Start of Conversation 2 in Figma
  '/frf10': defineRoute({
    selector: (state) => selectIsUserLoggedIn(state),
    resolve: (data) => {
      if (data === true) {
        return 'epo';
      }

      return 'qgp';
    },
  }),
  '/qgp': directLink('oca'),
  '/oca': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'offer_create_account___create_an_account_and_get_a') {
        return 'epo';
      }

      return 'browse';
    },
  }),
  '/browse': directLink('browse/plans'),
  // All routes below here need to be protected
  '/epo': directLink('visits'),
  '/visits': directLink('rx-details'),
  '/rx-details': directLink('preferred-providers'),
  '/preferred-providers': directLink('preferred-pharmacy'),
  '/preferred-pharmacy': directLink('nfq'),
  '/nfq': directLink('sab'),
  '/sab': directLink('tu'),
  '/tu': directLink('income-check'),
  '/income-check': directLink('epo2'),
  '/epo2': directLink('i40'),
  '/nma': sendOutCheck,
  // There spto routing is handled in AdditionalMedicalPlanForm
  '/i40': directLink('dob-lookup'),
  '/spto': sendOutCheck,
  // Start of Conversation 3 in Figma
  '/dob-lookup': medSuppEntry,
  // Start of complicated Eligibility routing
  // [overlapping iep and aep]
  '/two': directLink('wcy'),
  '/wcy': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => shoppingYearChoice(data).slug,
    effect: (data, dispatch) => {
      const { preferredShoppingYear } = shoppingYearChoice(data);
      dispatch(
        setDynamicValues({
          variableName: 'preferredShoppingYear',
          value: preferredShoppingYear,
        })
      );
      dispatch(
        setSelectedPlanYear(
          planYearForShoppingPreference(preferredShoppingYear)
        )
      );
    },
  }),
  '/fcty': directLink('ccr'),
  '/fcny': directLink('ccr'),
  '/fcby': directLink('ccr'),
  '/ccr': defineRoute({
    selector: (state) => ({
      preferredShoppingYear: selectPreferredShoppingYear(state),
      isComplicatedState: selectIsMedSuppComplicated(state),
      canSellMedSupp: selectCanSellMedSuppInState(state),
    }),
    resolve: ({
      preferredShoppingYear,
      isComplicatedState,
      canSellMedSupp,
    }) => {
      if (isComplicatedState || !canSellMedSupp) {
        return 'dsms';
      }

      return coverageYearSlug(preferredShoppingYear);
    },
  }),
  '/cty': defineRoute({
    selector: selectPreferredShoppingYear,
    resolve: (preferredShoppingYear) =>
      preferredShoppingYear === 'both' ? 'cby' : 'sm',
  }),
  '/cny': directLink('sm'),
  '/cby': directLink('sm'),
  // [iibi]
  '/iibi': checkMedSuppAvailability,
  '/pef': directLink('pew'),
  '/pew': directLink('sm'),
  // [ciesep]
  '/ciesep': directLink('seped'),
  '/seped': checkMedSuppAvailability,
  // [in-aep]
  '/in-aep': defineRoute({
    selector: (state) => ({
      ...selectSixMonthWindowCheck(state),
      isComplicatedState: selectIsMedSuppComplicated(state),
      canSellMedSupp: selectCanSellMedSuppInState(state),
    }),
    resolve: ({ isComplicatedState, canSellMedSupp, ...window }) => {
      if (isComplicatedState || !canSellMedSupp) {
        return 'dsms';
      }

      return sixMonthWindowSlug(window);
    },
  }),
  '/sbwyre': directLink('sbwed'),
  '/sbwnyre': directLink('sbwed'),
  '/aismw': directLink('sbwed'),
  '/sbwed': directLink('otp'),
  '/otp': sendOutCheck,
  '/tcmsp': sendOutCheck,
  // [in-ma-oep]
  '/in-ma-oep': defineRoute({
    selector: selectSixMonthWindowCheck,
    resolve: sixMonthWindowSlug,
  }),
  // [in-gep-sep]
  '/in-gep-sep': defineRoute({
    selector: selectSixMonthWindowCheck,
    resolve: sixMonthWindowSlug,
  }),
  '/6mw-ms': directLink('tcmsp'),
  '/sw-ms-nyr': directLink('tcmsp'),
  '/sw-ms-yr': directLink('tcmsp'),
  '/uet': sendOutCheck,
  '/dsms': directLink('gmar'),
  '/gmar': defineRoute({
    selector: (state) => ({
      clicked: selectLastButtonClicked(state),
      status: selectMedSuppEligibility(state).status,
      eligibilityCase: selectEligibilityCase(state),
      preferredShoppingYear: selectPreferredShoppingYear(state),
    }),
    resolve: ({ clicked, status, eligibilityCase, preferredShoppingYear }) => {
      if (clicked !== 'gmar___yes') {
        return 'pi';
      }
      if (eligibilityCase === 'inAEP') {
        return 'sm';
      }
      if (eligibilityCase === 'inIEPAEP') {
        return coverageYearSlug(preferredShoppingYear);
      }
      if (status === 'past') {
        return 'pew';
      }
      return 'pef';
    },
  }),
  // Send out Routes
  '/caq': defineRoute({
    selector: (state) => selectLastButtonClicked(state),
    resolve: (data) => {
      if (data === 'yes___userleaving___yes') {
        return 'create-account-prompt';
      }

      return 'pi';
    },
  }),
  '/pi': deadEnd,
  // Second Milestone
  '/sm': directLink('cei'),
  '/cei': directLink('ce'),
  '/ce': directLink('cemasma'),
  '/cemasma': defineRoute({
    selector: (state) => ({
      type: selectBestPlanOption(state).type,
      isHMOBetter: selectGuaranteedEnrollment(state).isHMOBetter,
      hasMapdAndMedSuppPlans: selectHasMapdAndMedSuppPlans(state),
      eligibleCoverageTypes: selectEligibleCoverageTypes(state),
    }),
    resolve: ({
      type,
      isHMOBetter,
      hasMapdAndMedSuppPlans,
      eligibleCoverageTypes,
    }) => {
      if (type === 'gap_pdp') {
        return 'lmsc';
      }

      // User was told they may be eligible for Med Supp (IEP/SEP), or we
      // already have both plan types. Run the Med Supp interview (hmsc)
      // instead of skipping to HMO savings.
      const eligibleForMedSupp = String(eligibleCoverageTypes).includes(
        'Med Supp'
      );
      if (hasMapdAndMedSuppPlans || eligibleForMedSupp) {
        return 'hmsc';
      }

      // We don't have Gap + PDP and they aren't in a Med Supp window
      if (isHMOBetter) {
        return 'hms';
      }
      return '3m';
    },
  }),
  '/lmsc': directLink('3m'),
  // See if Med Supp is worth it
  '/hmsc': directLink('mswe'),
  '/mswe': directLink('mt'),
  '/mt': directLink('mste'),
  '/mste': directLink('mst'),
  '/mst': directLink('mue'),
  '/mue': defineRoute({
    selector: (state) => ({
      ...selectGuaranteedEnrollment(state),
      ...selectMedSuppEligibility(state),
    }),
    resolve: ({
      isHMOBetter,
      hasMedSuppInterest,
      isYearRound,
      enrollmentWindow,
    }) => {
      if (!isYearRound && enrollmentWindow === 'IEP') {
        return 'ge10';
      }
      if (!hasMedSuppInterest && isHMOBetter) {
        return 'hms';
      }
      return '3m';
    },
  }),
  '/ge10': directLink('ge20'),
  '/ge20': defineRoute({
    selector: selectGuaranteedEnrollment,
    resolve: ({ isHMOBetter, hasMedSuppInterest }) =>
      !hasMedSuppInterest && isHMOBetter ? 'hms' : '3m',
  }),
  '/hms': directLink('3m'),
  // Third Milestone
  '/3m': defineRoute({
    selector: (state) => ({
      ...selectPathfinderThirdMilestone(state),
      plansByYear: selectRecommendationPlansByYear(state),
    }),
    resolve: () => 'rho',
    effect: (data, dispatch) => {
      const type: 'mapd' | 'gap_pdp' =
        !data.medSuppPlan ||
        (data.mapdPlan &&
          !data.stayInMedSupp &&
          data.bestOption !== 'gap_pdp' &&
          !data.snowbird &&
          !data.yesToMedSupp)
          ? 'mapd'
          : 'gap_pdp';

      dispatch(setRecommendedType(type));
      captureRecommendationReached(type);

      for (const plan of plansToCartForType(data.plansByYear, type)) {
        dispatch(addToCart(plan));
      }

      saveRecommendationType(type);
    },
  }),
  // Done!
  '/rho': directLink('cart'),
  // Back in flow - Broken right now :sadpand:
  '/bif': defineRoute({
    selector: (state) => selectUser(state).last_slug,
    resolve: (lastSlug) => {
      const redirects: Record<string, string> = { oms: 'mnl' };
      return lastSlug ? redirects[lastSlug] ?? lastSlug : null;
    },
  }),
};

export default routes;
